import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";

import Footer from "common/components/footer";
import Header from "common/components/header";
import SideNav from "common/components/side-nav";
import Individual from "marketplace/entities/individual/individual";
import { addIndividual } from "marketplace/entities/individual/realtime-store/individuals-redux-slice";
import IndividualProfile from "marketplace/values/individual-profile/individual-profile";
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSession } from "users/session/session-context";
import { useDialog } from "../providers/dialog";
import AiAssistant from "ai-assistant/view/ai-assistant";
import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor
} from '@dnd-kit/core';
import type { Coordinates } from '@dnd-kit/utilities';
import { restrictToWindowEdgesWithPadding } from 'common/dnd-kit-modifiers';
import Company from "marketplace/entities/company/company";
import CompanyProfile from "marketplace/values/company-profile/company-profile";

const Root = styled(Box)(() => ({
  alignItems: 'stretch',
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  width: '100%'
}));
const MainContainer = styled('main')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(7, 0, 0, 0)
  },
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'hidden',
  padding: theme.spacing(8, 0, 0, 0),
  width: '100%',
}));
const PageContent = styled('section')(() => ({
  flexGrow: 1,
  overflow: 'hidden',
}));

export default function Main() {
  const assistantRef = React.useRef<HTMLDivElement | null>(null);

  const [defaultCoords, setDefaultCoords] = React.useState<Coordinates>({
    x: globalThis.innerWidth - 200,
    y: globalThis.innerHeight - 100
  });
  const [chatBubbleCoords, setChatBubbleCoords] = React.useState<Coordinates | null>(null);
  const coords = chatBubbleCoords ?? defaultCoords;

  const [showNav, setShowNav] = React.useState(false);
  const [previousPathname, setPreviousPathname] = React.useState('');

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const location = useLocation();
  const { numDialogs, closeDialog } = useDialog();

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  // Close all dialogs when the route changes
  useEffect(() => {
    if (
      location.pathname !== previousPathname &&
      numDialogs > 0
    ) {
      closeDialog();
    }
    setPreviousPathname(location.pathname);
  }, [location.pathname, numDialogs, closeDialog]);

  // On initial mount
  useEffect(() => {
    if (session.user?.individualId && session.user.id && session.user.companyId && session.entities?.length) {
      const userIndividual = new Individual(
        session.user.individualId,
        session.user.id,
        new IndividualProfile(
          session.user.id,
          session.user.name.firstName ?? "",
          session.user.name.lastName ?? "",
          true
        ),
        new Company(
          session.user.companyId,
          session.entities[0]?.id,
          new CompanyProfile(session.user.companyName ?? ""),
          undefined,
          true
        ),
        true
      );
      dispatch(addIndividual(userIndividual));
    }

    const assistantElement = assistantRef.current;
    if (assistantElement) {
      const { width, height } = assistantElement.getBoundingClientRect();
      setDefaultCoords({
        x: globalThis.innerWidth - width - 16,
        y: globalThis.innerHeight - height - 16
      });
    }

    function handleWindowResize() {
      const bubbleEl = assistantRef.current;
      if (!bubbleEl) return;

      const { width, height } = bubbleEl.getBoundingClientRect();
      const padding = 16;
      const maxX = globalThis.innerWidth - width - padding;
      const maxY = globalThis.innerHeight - height - padding;

      // Re-calc the "default" bottom-right spot
      setDefaultCoords({ x: maxX, y: maxY });

      // Clamp to client area plus padding
      setChatBubbleCoords(prev => {
        if (!prev) return null;
        return {
          x: Math.min(Math.max(prev.x, padding), maxX),
          y: Math.min(Math.max(prev.y, padding), maxY),
        };
      });
    }

    globalThis.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    return () => globalThis.removeEventListener('resize', handleWindowResize);
  }, []);

  return (
    <Root>
      <CssBaseline />
      {/* <SystemNotificationBanner /> */}
      <Header toggleNavMenuClicked={() => setShowNav(!showNav)} />
      <SideNav
        showNav={showNav}
        navMenuClosed={() => setShowNav(false)}
      />
      <MainContainer>
        <PageContent>
          <Outlet />
          <DndContext
            sensors={sensors}
            onDragEnd={({ delta }) => {
              setChatBubbleCoords(prev => {
                const { x, y } = prev ?? defaultCoords;
                return {
                  x: x + delta.x,
                  y: y + delta.y,
                };
              });
            }}
            modifiers={[restrictToWindowEdgesWithPadding(16)]}
          >
            <AiAssistant
              ref={assistantRef}
              leftPos={coords.x}
              topPos={coords.y}
            />
          </DndContext>
        </PageContent>
        <Footer />
      </MainContainer>
    </Root>
  )
}
